import React, { useState, useEffect, useCallback, useRef } from 'react';

/**
 * IntroOverlay v4 - Cinématique avec déséquilibre maîtrisé
 *
 * Mise en scène :
 *  0 → 1s    : "Pendant que vous êtes…" blur→net
 *  1 → 2.4s  : "vos concurrents" + camera punch zoom + bar underline
 *  2.4 → 4.8s: Compteur DÉCALÉ à gauche (asymétrie premium)
 *              chaque +1 = notif synchronisée à position aléatoire
 *              camera continue à zoomer doucement
 *  4.8 → 5.6s: STOP NET — freeze frame, silence visuel
 *  5.6 → 6.6s: "Ils ne vous ont pas trouvé." + zoom out brutal (cinema release)
 *  6.6 → 7.4s: fade + sortie → hero
 */

// Positions variées pour les notifs (style notif réelle)
const NOTIF_POSITIONS = [
  { top: '12%', right: '6%', side: 'right' },
  { top: '38%', left: '5%', side: 'left' },
  { top: '18%', left: '8%', side: 'left' },
  { bottom: '22%', right: '7%', side: 'right' },
  { bottom: '32%', left: '6%', side: 'left' },
];

const IntroOverlay = ({ onComplete }) => {
  const [phase, setPhase] = useState(0);
  const [counter, setCounter] = useState(0);
  const [waves, setWaves] = useState(0);
  const [punchZoom, setPunchZoom] = useState(false); // zoom impact "concurrents"
  const [freezeFrame, setFreezeFrame] = useState(false); // STOP NET à +5
  const [notifs, setNotifs] = useState([]); // notifs avec positions variées
  const [isFadingOut, setIsFadingOut] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const hasInitialized = useRef(false);

  // Détection mobile
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 640px)');
    const update = () => setIsMobile(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);

  const pushNotif = (text, type, posIndex) => {
    const id = Date.now() + Math.random();
    const pos = NOTIF_POSITIONS[posIndex % NOTIF_POSITIONS.length];
    setNotifs((prev) => [...prev, { id, text, type, pos, mode: 'in' }]);
    // Slow fade out après 1.6s
    setTimeout(() => {
      setNotifs((prev) => prev.map((n) => (n.id === id ? { ...n, mode: 'out' } : n)));
    }, 1600);
    setTimeout(() => {
      setNotifs((prev) => prev.filter((n) => n.id !== id));
    }, 2700);
  };

  const completeAndExit = useCallback(() => {
    setIsVisible(false);
    // [DEV MODE] localStorage désactivé
    // localStorage.setItem('vyntraweb_intro_seen', 'true');
    // Notifie les autres composants que la cinématique est terminée
    window.dispatchEvent(new CustomEvent('vyntra:intro-complete'));
    if (onComplete) onComplete();
  }, [onComplete]);

  const skipIntro = useCallback(() => {
    if (isFadingOut) return;
    setIsFadingOut(true);
    setTimeout(completeAndExit, 700);
  }, [isFadingOut, completeAndExit]);

  // ── Lancement automatique de la cinématique ──
  useEffect(() => {
    if (hasInitialized.current) return;
    hasInitialized.current = true;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    // Phase 1 (0 → 1s)
    setTimeout(() => setPhase(1), 100);

    // Phase 2 (1 → 2.4s) — camera punch zoom sur "concurrents"
    setTimeout(() => setPhase(2), 1100);
    setTimeout(() => setPunchZoom(true), 1500);
    setTimeout(() => setPunchZoom(false), 2200);

    // Phase 3 (2.4 → 5.6s) — compteur décalé + notifs synchronisées (tempo plus calme)
    setTimeout(() => {
      setPhase(3);
      setCounter(1);
      setWaves((w) => w + 1);
      pushNotif('Recherche : "pizza thionville"', 'search', 0);
    }, 2400);
    setTimeout(() => {
      setCounter(2);
      setWaves((w) => w + 1);
      pushNotif('Client parti chez un concurrent', 'lost', 1);
    }, 3050);
    setTimeout(() => {
      setCounter(3);
      setWaves((w) => w + 1);
      pushNotif('Recherche : "pizzeria proche"', 'search', 2);
    }, 3700);
    setTimeout(() => {
      setCounter(4);
      setWaves((w) => w + 1);
      pushNotif('Client parti chez un concurrent', 'lost', 3);
    }, 4350);
    setTimeout(() => {
      setCounter(5);
      setWaves((w) => w + 1);
      pushNotif('Client parti chez un concurrent', 'lost', 4);
    }, 5000);

    // STOP NET à +5 (5.3 → 6.3s) — freeze frame, silence visuel
    setTimeout(() => setFreezeFrame(true), 5300);

    // Phase 4 (6.3s) — PHRASE CHOC : "Vos clients ne vous verront jamais."
    setTimeout(() => setPhase(4), 6300);

    // Phase 5 (8.8s) — FLASH + SOLUTION : "Alors soyez visible."
    setTimeout(() => setPhase(5), 8800);

    // Sortie rapide (10.2s → 10.8s)
    setTimeout(() => setIsFadingOut(true), 10200);
    setTimeout(completeAndExit, 10800);

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [completeAndExit]);

  // Skip sur clic/touche
  useEffect(() => {
    if (!isVisible) return;
    const handleClick = () => skipIntro();
    const handleKeyDown = (e) => {
      if (['Escape', 'Enter', ' '].includes(e.key)) {
        e.preventDefault();
        skipIntro();
      }
    };
    const timer = setTimeout(() => {
      window.addEventListener('click', handleClick);
      window.addEventListener('keydown', handleKeyDown);
    }, 400);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('click', handleClick);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isVisible, skipIntro]);

  if (!isVisible) return null;

  // Camera : zoom progressif continu pendant phases 1-3, punch sur concurrents, zoom OUT en phase 4-5
  const isP3 = phase === 3;
  const isP4 = phase === 4;
  const isP5 = phase >= 5;
  const dimTop = isP3 || isP4 || isP5;

  let sceneScale = 1;
  if (isFadingOut) sceneScale = isMobile ? 1.10 : 1.18;
  else if (isP5) sceneScale = isMobile ? 0.98 : 0.96; // Solution — scale neutre
  else if (isP4) sceneScale = isMobile ? 0.96 : 0.93; // ZOOM OUT BRUTAL — phrase choc
  else if (punchZoom) sceneScale = isMobile ? 1.035 : 1.07;
  else if (phase >= 3 && counter >= 4) sceneScale = isMobile ? 1.025 : 1.055;
  else if (phase >= 3 && counter >= 2) sceneScale = isMobile ? 1.02 : 1.04;
  else if (phase >= 3) sceneScale = isMobile ? 1.012 : 1.025;
  else if (phase >= 2) sceneScale = isMobile ? 1.008 : 1.015;
  else if (phase >= 1) sceneScale = 1.005;

  return (
    <div
      data-testid="intro-overlay"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background:
          'radial-gradient(ellipse at center, #1e293b 0%, #0f172a 55%, #050810 100%)',
        opacity: isFadingOut ? 0 : 1,
        transition: 'opacity 0.85s ease-out',
        overflow: 'hidden',
      }}
    >
      {/* Vignette */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background:
            'radial-gradient(circle at center, transparent 25%, rgba(0,0,0,0.7) 100%)',
          pointerEvents: 'none',
        }}
      />

      {/* ── SCÈNE CINÉMATIQUE ── */}

      {/* Glows ambiants - réduits/figés en freeze ou phase 4 */}
      <div
        style={{
          position: 'absolute',
          top: '-12rem',
          left: '-12rem',
          width: '36rem',
          height: '36rem',
          background: 'rgba(248, 113, 113, 0.10)',
          borderRadius: '50%',
          filter: 'blur(140px)',
          opacity: (isP4 || isP5) ? 0.15 : freezeFrame ? 0.5 : phase >= 2 ? 1 : 0.5,
          transition: 'opacity 1s ease-out',
          pointerEvents: 'none',
        }}
      />
      <div
        style={{
          position: 'absolute',
          bottom: '-12rem',
          right: '-12rem',
          width: '36rem',
          height: '36rem',
          background: 'rgba(248, 113, 113, 0.14)',
          borderRadius: '50%',
          filter: 'blur(140px)',
          opacity: (isP4 || isP5) ? 0.15 : freezeFrame ? 0.5 : phase >= 3 ? 1 : 0.5,
          transition: 'opacity 1s ease-out',
          pointerEvents: 'none',
        }}
      />
      <div
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '28rem',
          height: '28rem',
          background: isP5 ? 'rgba(0, 230, 255, 0.18)' : 'rgba(6, 182, 212, 0.09)',
          borderRadius: '50%',
          filter: 'blur(160px)',
          opacity: isP5 ? 0.85 : isP4 ? 0.6 : 0,
          transition: 'opacity 1.1s ease-out',
          pointerEvents: 'none',
        }}
      />

      {/* Notifs : stack toast en bas sur mobile / positions variées sur desktop */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          overflow: 'hidden',
          opacity: (isP4 || isP5) ? 0 : 1,
          transition: 'opacity 0.6s ease-out',
        }}
      >
        {/* Mobile : stack vertical centré en bas (au-dessus du skip hint) */}
        {isMobile && (
          <div
            style={{
              position: 'absolute',
              left: '50%',
              bottom: '4.5rem',
              transform: 'translateX(-50%)',
              display: 'flex',
              flexDirection: 'column-reverse',
              gap: '0.45rem',
              alignItems: 'center',
              width: '92vw',
              maxWidth: '92vw',
            }}
          >
            {notifs.slice(-3).map((notif) => {
              const isSearch = notif.type === 'search';
              return (
                <div
                  key={notif.id}
                  style={{
                    fontSize: '0.72rem',
                    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                    color: isSearch ? 'rgba(34,211,238,0.85)' : 'rgba(252,165,165,0.95)',
                    padding: '0.42rem 0.75rem',
                    border: `1px solid ${isSearch ? 'rgba(34,211,238,0.4)' : 'rgba(248,113,113,0.5)'}`,
                    borderRadius: '8px',
                    background: isSearch
                      ? 'linear-gradient(135deg, rgba(15,23,42,0.92) 0%, rgba(8,47,73,0.65) 100%)'
                      : 'linear-gradient(135deg, rgba(15,23,42,0.95) 0%, rgba(76,5,25,0.6) 100%)',
                    backdropFilter: 'blur(8px)',
                    WebkitBackdropFilter: 'blur(8px)',
                    whiteSpace: 'nowrap',
                    maxWidth: '100%',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    lineHeight: 1.3,
                    boxShadow: isSearch
                      ? '0 6px 22px rgba(34,211,238,0.2)'
                      : '0 6px 22px rgba(248,113,113,0.28)',
                    animation:
                      notif.mode === 'in'
                        ? `notifSlideUp 0.4s cubic-bezier(0.22, 1, 0.36, 1) forwards`
                        : `notifFadeOut 0.7s ease-out forwards`,
                  }}
                >
                  {isSearch ? '> ' : '× '}
                  {notif.text}
                </div>
              );
            })}
          </div>
        )}

        {/* Desktop : positions variées (style notif réelle) */}
        {!isMobile &&
          notifs.map((notif) => {
            const isSearch = notif.type === 'search';
            const slideAxis = notif.pos.side === 'left' ? -50 : 50;
            return (
              <div
                key={notif.id}
                style={{
                  position: 'absolute',
                  top: notif.pos.top,
                  bottom: notif.pos.bottom,
                  left: notif.pos.left,
                  right: notif.pos.right,
                  fontSize: '0.85rem',
                  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                  color: isSearch ? 'rgba(34,211,238,0.8)' : 'rgba(252,165,165,0.92)',
                  padding: '0.55rem 0.95rem',
                  border: `1px solid ${isSearch ? 'rgba(34,211,238,0.4)' : 'rgba(248,113,113,0.5)'}`,
                  borderRadius: '8px',
                  background: isSearch
                    ? 'linear-gradient(135deg, rgba(15,23,42,0.85) 0%, rgba(8,47,73,0.6) 100%)'
                    : 'linear-gradient(135deg, rgba(15,23,42,0.9) 0%, rgba(76,5,25,0.55) 100%)',
                  backdropFilter: 'blur(8px)',
                  WebkitBackdropFilter: 'blur(8px)',
                  whiteSpace: 'nowrap',
                  lineHeight: 1.35,
                  boxShadow: isSearch
                    ? '0 8px 28px rgba(34,211,238,0.18), 0 0 16px rgba(34,211,238,0.12)'
                    : '0 8px 28px rgba(248,113,113,0.28), 0 0 22px rgba(248,113,113,0.2)',
                  animation:
                    notif.mode === 'in'
                      ? `notifSlideIn 0.45s cubic-bezier(0.22, 1, 0.36, 1) forwards`
                      : `notifFadeOut 1s ease-out forwards`,
                  ['--slide-x']: `${slideAxis}px`,
                }}
              >
                {isSearch ? '> ' : '× '}
                {notif.text}
              </div>
            );
          })}
      </div>

      {/* Scène avec zoom caméra global */}
      <div
        style={{
          position: 'relative',
          zIndex: 10,
          textAlign: 'center',
          padding: '0 1.5rem',
          maxWidth: '52rem',
          width: '100%',
          transform: `scale(${sceneScale})`,
          transition: freezeFrame
            ? 'transform 0.15s ease-out'
            : 'transform 1.2s cubic-bezier(0.22, 1, 0.36, 1)',
        }}
      >
        {/* Phase 1 - texte haut (centré) */}
        <p
          style={{
            fontSize: 'clamp(1.4rem, 3.5vw, 2.25rem)',
            fontWeight: 500,
            color: 'rgba(255,255,255,0.95)',
            marginBottom: '1.25rem',
            opacity: (isP4 || isP5) ? 0 : phase >= 1 ? (dimTop ? 0.18 : 1) : 0,
            transform: phase >= 1 ? (dimTop ? 'scale(0.88)' : 'scale(1)') : 'scale(0.92)',
            transition:
              'opacity 0.9s ease-out, transform 0.9s ease-out',
            letterSpacing: '0.02em',
          }}
        >
          Pendant que vous êtes sur cette page…
        </p>

        {/* Phase 2 - "vos concurrents" (centré) */}
        <p
          style={{
            fontSize: 'clamp(1.75rem, 5vw, 3.25rem)',
            fontWeight: 700,
            color: 'rgba(255,255,255,0.95)',
            marginBottom: '2.75rem',
            opacity: (isP4 || isP5) ? 0 : phase >= 2 ? (dimTop ? 0.28 : 1) : 0,
            transform: phase >= 2 ? 'translateY(0)' : 'translateY(1.5rem)',
            transition: 'opacity 0.8s ease-out, transform 0.7s ease-out',
            lineHeight: 1.2,
          }}
        >
          …des clients choisissent{' '}
          <span
            style={{
              position: 'relative',
              color: '#FCA5A5',
              fontWeight: 800,
              display: 'inline-block',
              textShadow: punchZoom
                ? '0 0 32px rgba(248,113,113,1), 0 0 64px rgba(248,113,113,0.7), 0 0 100px rgba(248,113,113,0.4)'
                : phase >= 2
                ? '0 0 20px rgba(248,113,113,0.7), 0 0 40px rgba(248,113,113,0.4)'
                : 'none',
              animation: phase === 2 ? 'concurrentGlow 1.4s ease-in-out infinite' : 'none',
            }}
          >
            vos concurrents.
            <span
              style={{
                position: 'absolute',
                left: 0,
                bottom: '-6px',
                height: '3px',
                width: '100%',
                background: 'linear-gradient(90deg, transparent 0%, #F87171 50%, transparent 100%)',
                transform: punchZoom ? 'scaleX(1)' : 'scaleX(0)',
                transformOrigin: 'center',
                transition: 'transform 0.5s cubic-bezier(0.22, 1, 0.36, 1)',
                boxShadow: '0 0 12px rgba(248,113,113,0.8)',
              }}
            />
          </span>
        </p>

        {/* Phase 3 - Compteur DÉCALÉ À GAUCHE (asymétrie premium) — centré sur mobile */}
        <div
          style={{
            display: 'flex',
            justifyContent: isMobile ? 'center' : 'flex-start',
            paddingLeft: isMobile ? '0' : 'clamp(0px, 6vw, 4rem)',
            marginBottom: isMobile ? '1.25rem' : '2.5rem',
            opacity: (isP4 || isP5) ? 0 : phase >= 3 ? 1 : 0,
            transform:
              phase >= 3
                ? freezeFrame
                  ? 'translateY(0) scale(1.02)'
                  : 'translateY(0) scale(1)'
                : 'translateY(1rem) scale(0.92)',
            transition:
              'opacity 0.9s ease-out, transform 0.9s cubic-bezier(0.22, 1, 0.36, 1)',
          }}
        >
          <div
            style={{
              position: 'relative',
              padding: isMobile ? '1.25rem 1.5rem' : '2rem 3rem',
              borderRadius: isMobile ? '18px' : '24px',
              background:
                'linear-gradient(135deg, rgba(248,113,113,0.14) 0%, rgba(15,23,42,0.7) 100%)',
              border: freezeFrame
                ? '1.5px solid rgba(248,113,113,0.7)'
                : '1px solid rgba(248,113,113,0.4)',
              boxShadow: freezeFrame
                ? '0 0 120px rgba(248,113,113,0.55), 0 0 200px rgba(248,113,113,0.25), inset 0 1px 0 rgba(255,255,255,0.12)'
                : '0 0 80px rgba(248,113,113,0.35), 0 0 140px rgba(248,113,113,0.15), inset 0 1px 0 rgba(255,255,255,0.1)',
              backdropFilter: 'blur(18px)',
              WebkitBackdropFilter: 'blur(18px)',
              transform: isMobile
                ? 'rotate(0deg)'
                : freezeFrame
                ? 'rotate(-1deg)'
                : 'rotate(-0.5deg)',
              transition: 'transform 0.6s ease-out',
            }}
          >
            {/* Ondes lumineuses */}
            <div
              key={`wave-${waves}`}
              style={{
                position: 'absolute',
                inset: 0,
                borderRadius: '24px',
                border: '2px solid rgba(248,113,113,0.6)',
                pointerEvents: 'none',
                opacity: 0,
                animation: waves > 0 ? 'ringWave 0.95s cubic-bezier(0.22, 1, 0.36, 1) forwards' : 'none',
              }}
            />
            <div
              key={`wave2-${waves}`}
              style={{
                position: 'absolute',
                inset: 0,
                borderRadius: '24px',
                border: '1px solid rgba(248,113,113,0.4)',
                pointerEvents: 'none',
                opacity: 0,
                animation: waves > 0 ? 'ringWave 1.2s cubic-bezier(0.22, 1, 0.36, 1) 0.15s forwards' : 'none',
              }}
            />
            {/* Flash overlay */}
            <div
              key={`flash-${waves}`}
              style={{
                position: 'absolute',
                inset: 0,
                borderRadius: '24px',
                background: 'rgba(255,255,255,0.18)',
                pointerEvents: 'none',
                opacity: 0,
                animation: waves > 0 ? 'flashCard 0.5s ease-out forwards' : 'none',
              }}
            />

            <div
              style={{
                display: 'flex',
                alignItems: 'baseline',
                gap: '0.85rem',
                position: 'relative',
                zIndex: 2,
              }}
            >
              <span
                key={`num-${counter}`}
                style={{
                  fontSize: 'clamp(5rem, 14vw, 9rem)',
                  fontWeight: 900,
                  color: 'white',
                  textShadow:
                    '0 0 40px rgba(248,113,113,0.7), 0 0 90px rgba(248,113,113,0.35)',
                  animation: counter > 0 ? 'counterPulseStrong 0.55s cubic-bezier(0.22, 1, 0.36, 1)' : 'none',
                  fontVariantNumeric: 'tabular-nums',
                  lineHeight: 1,
                  display: 'inline-block',
                }}
              >
                +{counter}
              </span>
              <span
                style={{
                  fontSize: 'clamp(1.1rem, 2.5vw, 1.5rem)',
                  color: '#cbd5e1',
                  fontWeight: 500,
                }}
              >
                client{counter > 1 ? 's' : ''}{' '}
                <span style={{ color: '#F87171', fontWeight: 700 }}>
                  perdu{counter > 1 ? 's' : ''}
                </span>
              </span>
            </div>
          </div>
        </div>

        {/* Phase 4 - PHRASE CHOC : "Vos clients ne vous verront jamais." */}
        <p
          style={{
            position: (isP4 || isP5) ? 'absolute' : 'relative',
            left: (isP4 || isP5) ? '50%' : 'auto',
            top: (isP4 || isP5) ? '50%' : 'auto',
            transform: (isP4 || isP5) ? 'translate(-50%, -50%)' : 'none',
            width: (isP4 || isP5) ? '100%' : 'auto',
            fontSize: 'clamp(1.7rem, 4.5vw, 2.6rem)',
            fontWeight: 600,
            color: 'rgba(255,255,255,0.97)',
            opacity: isP4 && !isP5 ? 1 : 0,
            transition: isP5 ? 'opacity 0.3s ease-out' : 'opacity 0.8s ease-in',
            lineHeight: 1.3,
            letterSpacing: '0.015em',
            margin: 0,
            textAlign: 'center',
            padding: '0 1.5rem',
          }}
        >
          Vos clients ne vous{' '}
          <span
            style={{
              color: '#FCA5A5',
              fontWeight: 800,
              textShadow: isP4 ? '0 0 32px rgba(248,113,113,0.7)' : 'none',
              animation: isP4 && !isP5 ? 'softBreathe 2.4s ease-in-out infinite' : 'none',
              display: 'inline-block',
              willChange: 'opacity',
            }}
          >
            verront jamais.
          </span>
        </p>

        {/* Lueur bleue intense derrière "soyez visible" */}
        <div
          style={{
            position: 'absolute',
            left: '50%',
            top: '50%',
            transform: 'translate(-50%, -50%)',
            width: 'min(120vw, 75rem)',
            height: 'min(90vh, 45rem)',
            background:
              'radial-gradient(ellipse at center, rgba(0,230,255,0.32) 0%, rgba(0,180,255,0.22) 25%, rgba(0,150,255,0.13) 50%, rgba(0,120,255,0.06) 70%, transparent 88%)',
            filter: 'blur(70px)',
            opacity: isP5 && !isFadingOut ? 1 : 0,
            transition: 'opacity 0.7s ease-out',
            pointerEvents: 'none',
            zIndex: 1,
            animation: isP5 && !isFadingOut ? 'cyanBreathe 2.6s ease-in-out infinite' : 'none',
          }}
        />

        {/* Phase 5 - SOLUTION : "Alors soyez visible." */}
        <p
          style={{
            position: 'absolute',
            left: '50%',
            top: '50%',
            transform: isP5 ? 'translate(-50%, -50%) scale(1)' : 'translate(-50%, -50%) scale(0.92)',
            width: '100%',
            fontSize: 'clamp(2rem, 5.5vw, 3.2rem)',
            fontWeight: 700,
            color: 'rgba(255,255,255,0.97)',
            opacity: isP5 ? 1 : 0,
            transition: 'opacity 0.6s ease-out 0.2s, transform 0.6s ease-out 0.2s',
            lineHeight: 1.3,
            letterSpacing: '0.02em',
            margin: 0,
            textAlign: 'center',
            padding: '0 1.5rem',
          }}
        >
          Alors{' '}
          <span
            style={{
              color: '#00E6FF',
              fontWeight: 900,
              textShadow: isP5
                ? '0 0 18px rgba(0,230,255,0.7), 0 0 50px rgba(0,200,255,0.55), 0 0 110px rgba(0,170,255,0.4), 0 0 200px rgba(0,140,255,0.3)'
                : 'none',
              display: 'inline-block',
            }}
          >
            soyez visible.
          </span>
        </p>
      </div>

      {/* Skip hint */}
      <div
        style={{
          position: 'absolute',
          bottom: '2rem',
          left: 0,
          right: 0,
          textAlign: 'center',
          opacity: phase >= 1 && !isFadingOut ? 0.5 : 0,
          transition: 'opacity 0.5s',
          pointerEvents: 'none',
        }}
      >
        <p
          style={{
            fontSize: '0.78rem',
            color: '#64748b',
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
          }}
        >
          Cliquez pour passer
        </p>
      </div>

      <style>{`
        @keyframes counterPulseStrong {
          0%   { transform: scale(0.6); opacity: 0; }
          35%  { transform: scale(1.18); opacity: 1; }
          65%  { transform: scale(0.95); }
          100% { transform: scale(1); opacity: 1; }
        }
        @keyframes ringWave {
          0%   { transform: scale(1); opacity: 0.85; }
          100% { transform: scale(1.55); opacity: 0; }
        }
        @keyframes flashCard {
          0%   { opacity: 0; }
          25%  { opacity: 1; }
          100% { opacity: 0; }
        }
        @keyframes concurrentGlow {
          0%, 100% { opacity: 0.85; }
          50%      { opacity: 1; }
        }
        @keyframes softBreathe {
          0%, 100% { opacity: 0.8; }
          50%      { opacity: 1; }
        }
        @keyframes notifSlideIn {
          0%   { opacity: 0; transform: translateX(var(--slide-x)) scale(0.92); }
          60%  { opacity: 1; }
          100% { opacity: 1; transform: translateX(0) scale(1); }
        }
        @keyframes notifSlideUp {
          0%   { opacity: 0; transform: translateY(20px) scale(0.95); }
          60%  { opacity: 1; }
          100% { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes notifFadeOut {
          0%   { opacity: 1; transform: translateX(0) scale(1); }
          100% { opacity: 0; transform: translateX(0) scale(0.95) translateY(-4px); }
        }
        @keyframes cyanBreathe {
          0%, 100% { opacity: 0.85; transform: translate(-50%, -50%) scale(1); }
          50%      { opacity: 1; transform: translate(-50%, -50%) scale(1.06); }
        }
        @keyframes flashSwitch {
          0%   { opacity: 0; }
          20%  { opacity: 0.15; }
          100% { opacity: 0; }
        }
        @keyframes fadeInAmorce {
          0%   { opacity: 0; transform: scale(0.95); }
          100% { opacity: 1; transform: scale(1); }
        }
        @keyframes pulseAmorce {
          0%, 100% { transform: scale(1); opacity: 0.7; }
          50%      { transform: scale(1.08); opacity: 1; }
        }
      `}</style>
    </div>
  );
};

export default IntroOverlay;
